import {
  CopyOutlined,
  FileTextOutlined,
  MessageOutlined,
  ShoppingOutlined,
  StarOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import * as api from '@apis/admin-user';
import * as authApi from '@apis/auth';
import * as statisticsApi from '@apis/statistics';
import { ReactComponent as ChevronDownIcon } from '@assets/images/app/chevron-down-nutral90.svg';
import { ReactComponent as ChevronUpIcon } from '@assets/images/app/chevron-up-nutral90.svg';
import { ReactComponent as AttachmentIcon } from '@assets/images/app/icon-attachment.svg';
import PartnerProfileHeader1 from '@assets/images/app/partner-profile-header-1.png';
import PartnerProfileHeader2 from '@assets/images/app/partner-profile-header-2.png';
import PartnerProfileHeader3 from '@assets/images/app/partner-profile-header-3.png';
import PartnerProfileHeader4 from '@assets/images/app/partner-profile-header-4.png';
import PartnerProfileHeader5 from '@assets/images/app/partner-profile-header-5.png';
import PartnerProfileHeader6 from '@assets/images/app/partner-profile-header-6.png';
import PartnerProfileHeader7 from '@assets/images/app/partner-profile-header-7.png';
import PartnerProfileHeader8 from '@assets/images/app/partner-profile-header-8.png';
import PartnerProfileHeader9 from '@assets/images/app/partner-profile-header-9.png';
import { ReactComponent as QualifiedIcon } from '@assets/images/app/qualified-check-circle.svg';
import TutorDefaultAvatar from '@assets/images/app/tutorintro.png';
import { ReactComponent as ToolbarImageIcon } from '@assets/images/editor/attach-image.svg';
import { Divider } from '@components/common/Divider';
import { UserCreateModal } from '@components/user/UserCreateModal';
import { UserEditModal } from '@components/user/UserEditModal';
import { UserMembershipEditModal } from '@components/user/UserMembershipEditModal';
import UserPointEditModal from '@components/user/UserPointEditModal';
import { PARTNER_ROLE_LABELS, Roles, ROLE_LABELS } from '@consts/role';
import { useAppSocket } from '@hooks/appSocket';
import { useAuthentication } from '@hooks/authentication';
import { AdminUser, ExcellentPartner, PartnerStatistics, ProfileApprovalState } from '@models/auth';
import { ProductModel } from '@models/product';
import { useAdminPartnerUsersStore } from '@stores/adminPartnerUsersStore';
import { useContentLoaderStore } from '@stores/contentLoaderStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isPartnerRole } from '@utils/common';
import { degreeValue, formatPhoneNumber, getAddressText } from '@utils/dataTransform';
import { localTimeToUtcISOString, utcTimeToLocalISOString } from '@utils/date';
import { cdnImageUrl } from '@utils/image';
import { PARTNER_URL, WEB_URL } from '@variables';
import { Badge, Button, DatePicker, DatePickerProps, Input, Modal, Popover, Select, Switch, Table } from 'antd';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import moment from 'moment';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { useEffect, useRef, useState } from 'react';
import LinesEllipsis from 'react-lines-ellipsis';
import { PhotoView } from 'react-photo-view';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const passLabel = (passed: boolean | null | undefined, evaluated: boolean) => {
  if (!evaluated || passed == null) {
    return '-';
  }
  return passed ? '통과' : '미달';
};

const ExcellentPartnerCriteriaTable: React.FC<{
  excellentPartner?: ExcellentPartner;
}> = ({ excellentPartner }) => {
  const metrics = excellentPartner?.metrics;
  const evaluated = !!excellentPartner?.evaluatedAt || !!metrics;
  const faultRate = metrics?.partnerFaultRate3m;
  const rows = [
    {
      key: 'rating',
      group: '체험 만족도',
      rowSpan: 3,
      rule: '최근 3개월 후기의 평균 평점',
      value: metrics?.reviewAvg3m == null ? '-' : `${metrics.reviewAvg3m}점`,
      standard: '4.8점 이상',
      passed: metrics?.reviewAvg3m != null && metrics.reviewAvg3m >= 4.8,
    },
    {
      key: 'recent-reviews',
      group: '체험 만족도',
      rowSpan: 0,
      rule: '최근 3개월 후기',
      value: `${metrics?.reviewCount3m ?? 0}건`,
      standard: '3건 이상',
      passed: (metrics?.reviewCount3m ?? 0) >= 3,
    },
    {
      key: 'reviews',
      group: '체험 만족도',
      rowSpan: 0,
      rule: '전체 상품 누적 후기',
      value: `${metrics?.reviewCountAll ?? 0}건`,
      standard: '10건 이상',
      passed: (metrics?.reviewCountAll ?? 0) >= 10,
    },
    {
      key: 'success',
      group: '예약 성사',
      rowSpan: 1,
      rule: '최근 3개월 파트너 사유 무산',
      value: faultRate == null ? '-' : `${faultRate}% (${metrics?.partnerFaultCount3m ?? 0}/${metrics?.sales3m ?? 0})`,
      standard: '10% 이하',
      passed: faultRate == null ? null : faultRate <= 10,
    },
    {
      key: 'sales',
      group: '최근 활동',
      rowSpan: 1,
      rule: '최근 3개월 결제',
      value: `${metrics?.sales3m ?? 0}건`,
      standard: '5건 이상',
      passed: (metrics?.sales3m ?? 0) >= 5,
    },
  ];

  return (
    <div style={{ width: 720 }}>
      <Table
        size="small"
        pagination={false}
        rowKey="key"
        dataSource={rows}
        columns={[
          {
            title: '구분',
            dataIndex: 'group',
            width: 110,
            onCell: (row) => ({ rowSpan: row.rowSpan }),
          },
          { title: '기준', dataIndex: 'rule', width: 220 },
          { title: '파트너 값', dataIndex: 'value', width: 160 },
          { title: '기준 값', dataIndex: 'standard', width: 120 },
          {
            title: '통과 여부',
            width: 80,
            render: (_, row) => {
              const label = passLabel(row.passed, evaluated);
              return (
                <span style={{ color: label === '통과' ? '#389e0d' : label === '미달' ? '#cf1322' : undefined }}>
                  {label}
                </span>
              );
            },
          },
        ]}
      />
      <div style={{ marginTop: 8, color: '#595959', fontSize: 12 }}>
        선정 일시 {excellentPartner?.selectedAt ? dayjs(excellentPartner.selectedAt).format('YYYY-MM-DD HH:mm') : '-'}
        {' · '}
        유지 기한{' '}
        {excellentPartner?.retainedUntil ? dayjs(excellentPartner.retainedUntil).format('YYYY-MM-DD HH:mm') : '-'}
      </div>
    </div>
  );
};

const HEADER_IMAGES = [
  PartnerProfileHeader1,
  PartnerProfileHeader2,
  PartnerProfileHeader3,
  PartnerProfileHeader4,
  PartnerProfileHeader5,
  PartnerProfileHeader6,
  PartnerProfileHeader7,
  PartnerProfileHeader8,
  PartnerProfileHeader9,
];

export const PartnerUsersPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [pointEditUser, setPointEditUser] = useState<AdminUser>();
  const [selectedTutorId, setSelectedTutorId] = useState<string>();
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const [editUserId, setEditUserId] = useState<string>();
  const [editMembershipUserId, setEditMembershipUserId] = useState<string>();
  const [showUserCreate, setShowUserCreate] = useState<boolean>(false);
  const [showExperiences, setShowExperiences] = useState<boolean>(false);
  const [showWarningProductsModal, setShowWarningProductsModal] = useState<boolean>(false);
  const headerImageRef = useRef<string>(HEADER_IMAGES[Math.floor(Math.random() * 9)]);
  const [isGreetingClamped, setIsGreetingClamped] = useState<boolean>(false);
  const [expandGreeting, setExpandGreeting] = useState<boolean>(false);
  const appSocket = useAppSocket();
  const store = useAdminPartnerUsersStore();
  const loader = useContentLoaderStore();
  useAuthentication();
  const dialog = useOkCancelDialog();
  const membership = searchParams.get('membership');
  const userSearchQuery = useQuery({
    queryKey: [`admin-search-users`, JSON.stringify(store.searchQuery)],
    queryFn: () =>
      api.searchUsers({
        ...store.searchQuery,
        roles: [Roles.PARTNER, Roles.TUTOR, Roles.SELLER, Roles.INSTRUCTOR, Roles.EDITOR, Roles.MEMBERSHIP_PARTNER],
      }),
  });

  const [selectionRunning, setSelectionRunning] = useState(false);
  const evaluatedAtBeforeRun = useRef<string | null | undefined>(undefined);
  const partnersCountsQuery = useQuery({
    queryKey: [`partners-counts`],
    queryFn: () => statisticsApi.getPartnersCounts(),
    refetchInterval: selectionRunning ? 3000 : false,
  });
  const excellentPartnerMutation = useMutation({
    mutationFn: (body: { userId: string; selected: boolean }) => api.setExcellentPartner(body),
    onSuccess: () => {
      userSearchQuery.refetch();
      partnersCountsQuery.refetch();
    },
    onError: () => toast.error('우수파트너 선정을 변경하지 못했습니다.'),
  });
  const runSelectionMutation = useMutation({
    mutationFn: () => api.runExcellentPartnerSelection(),
    onSuccess: (response) => {
      if (response.data?.result === 'ALREADY_RUNNING') {
        toast.info('이미 우수파트너 선정이 실행 중입니다.');
        return;
      }
      evaluatedAtBeforeRun.current = partnersCountsQuery.data?.data?.excellentPartnerEvaluatedAt || null;
      setSelectionRunning(true);
      toast.success('우수파트너 선정을 시작했습니다.');
    },
    onError: () => toast.error('우수파트너 선정을 시작하지 못했습니다.'),
  });

  useEffect(() => {
    if (!selectionRunning) {
      return;
    }
    const next = partnersCountsQuery.data?.data?.excellentPartnerEvaluatedAt;
    if (next && next !== evaluatedAtBeforeRun.current) {
      setSelectionRunning(false);
      userSearchQuery.refetch();
      toast.success('우수파트너 선정이 완료되었습니다.');
    }
  }, [partnersCountsQuery.data?.data?.excellentPartnerEvaluatedAt, selectionRunning]);

  useEffect(() => {
    if (!selectionRunning) {
      return;
    }
    const timer = window.setTimeout(() => setSelectionRunning(false), 3 * 60 * 1000);
    return () => window.clearTimeout(timer);
  }, [selectionRunning]);

  const userQuery = useQuery({
    queryKey: [`user-item-${selectedTutorId}`],
    queryFn: () => api.getUser(selectedTutorId!),
    enabled: !!selectedTutorId,
  });

  const loginAsMutation = useMutation({
    mutationFn: authApi.loginAsUser,
  });

  const deleteUserMutation = useMutation({
    mutationFn: api.deleteUser,
    onSuccess: () => {
      userSearchQuery.refetch();
      toast.dark('탈퇴 처리 되었습니다.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
      });
    },
  });

  const restoreDeletedUserMutation = useMutation({
    mutationFn: api.restoreDeletedUser,
    onSuccess: () => {
      toast.dark('탈퇴 복구 되었습니다.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
      });
      userSearchQuery.refetch();
    },
  });

  const partnerProfileMutation = useMutation({
    mutationFn: api.updatePartnerProfile,
    onSuccess: async () => {
      toast.dark('파트너 프로필을 수정했습니다.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        bodyStyle: {
          color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
          textAlign: 'center',
          fontFamily: 'Pretendard',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '20px',
        },
        theme: 'dark',
      });
    },
  });

  const userInfo = userQuery.data?.data;

  const partnerAcamemics = userInfo?.partnerProfile?.academics || [];

  const partnerCareers = userInfo?.partnerProfile?.careers || [];

  const partnerCertificates = userInfo?.partnerProfile?.certificates || [];

  useEffect(() => {
    if (membership) {
      store.updateSearchQuery({ membership });
    }
  }, [membership]);

  useEffect(() => {
    if (store.searchQuery.keyword && !searchKeyword) {
      setSearchKeyword(store.searchQuery.keyword);
    }
  }, [store.searchQuery.keyword]);

  useEffect(() => {
    const searchResult = userSearchQuery.data?.data;
    if (searchResult) {
      store.setAdminPartnerUsersContainer(searchResult);
    }
  }, [userSearchQuery.data?.data]);

  useEffect(() => {
    if (partnersCountsQuery.data?.data?.warningProducts && partnersCountsQuery.data.data.warningProducts.length > 0) {
      setShowWarningProductsModal(true);
    }
  }, [partnersCountsQuery.data?.data?.warningProducts]);

  useEffect(() => {
    appSocket.socket.addSocketEventListener('upload_excel_status', 'upload_excel_status', (data) => {
      const status = (data.status || '').toLowerCase();
      if (status === 'done') {
        userSearchQuery.refetch();
      }
      if (status === 'done' || status === 'error') {
        loader.off();
      }

      if (data.status) {
        toast.error(data.message, {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          pauseOnFocusLoss: false,
          draggable: true,
          progress: undefined,
          theme: 'light',
        });
      }
    });
  }, []);

  window.onbeforeunload = () => {
    appSocket.socket.removeSocketEventListener('upload_excel_status', 'upload_excel_status');
  };

  const getExcelFile = async () => {
    const fileName = `igogo-users-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
    const blob = await api.getSearchUserssExcelFile(store.searchQuery);
    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      // IE variant
      (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
    } else {
      const url = window.URL.createObjectURL(blob.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode && link.parentNode.removeChild(link);
    }
  };

  const onChangeStartDate: DatePickerProps['onChange'] = (date) => {
    if (date) {
      store.setStartDate(localTimeToUtcISOString(new Date(date.toISOString()), { timeToZero: true }));
    } else {
      store.setStartDate(null);
    }
  };

  const onChangeEndDate: DatePickerProps['onChange'] = (date) => {
    if (date) {
      store.setEndDate(localTimeToUtcISOString(new Date(date.toISOString()), { timeToZero: true }));
    } else {
      store.setEndDate(null);
    }
  };

  const TABLE_COLUMNS: ColumnsType<AdminUser> = [
    {
      title: '기능',
      align: 'center',
      width: '140px',
      dataIndex: '_id',
      render: (_id, record) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(_id);
              toast.dark('사용자 ID를 클립보드에 복사했습니다', {
                position: 'top-center',
                autoClose: 3000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                pauseOnFocusLoss: false,
                draggable: true,
                progress: undefined,
                bodyStyle: {
                  color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                  textAlign: 'center',
                  fontFamily: 'Pretendard',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '20px',
                },
                theme: 'dark',
              });
            }}>
            ID 복사
          </Button>
          <Button
            onClick={() => {
              dialog.open({
                title: '웹으로 이동',
                content: `선택하신 사용자로 로그인 후 웹으로 이동합니다.\n(어드민 계정은 로그아웃 됩니다)`,
                type: 'ok_cancel',
                onConfirm: () => {
                  loginAsMutation.mutate(_id, {
                    onSuccess: () => {
                      window.open(WEB_URL);
                    },
                  });
                },
              });
            }}>
            웹 로그인
          </Button>
          {isPartnerRole(record) && (
            <Button
              onClick={() => {
                dialog.open({
                  title: '파트너 센터로 이동',
                  content: `선택하신 사용자로 로그인 후 파트너 센터로 이동합니다.\n(어드민 계정은 로그아웃 됩니다)`,
                  type: 'ok_cancel',
                  onConfirm: () => {
                    loginAsMutation.mutate(_id, {
                      onSuccess: () => {
                        window.open(`${PARTNER_URL}/dashboard`);
                      },
                    });
                  },
                });
              }}>
              파트너센터 로그인
            </Button>
          )}
        </div>
      ),
    },
    {
      title: '수정',
      align: 'center',
      width: '80px',
      render: (record) => {
        const _id = record._id;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
            <Button
              onClick={() => {
                setEditUserId(_id);
              }}>
              수정
            </Button>
            {record.deletedAt && (
              <Button
                type="dashed"
                onClick={() => {
                  dialog.open({
                    title: '탈퇴 복구',
                    content: '탈퇴 유저를 복구하시겠습니까?',
                    type: 'ok_cancel',
                    onConfirm: () => {
                      restoreDeletedUserMutation.mutate(_id);
                    },
                  });
                }}>
                탈퇴 복구
              </Button>
            )}
            {!record.deletedAt && (
              <Button
                danger
                onClick={() => {
                  dialog.open({
                    title: '탈퇴 처리',
                    content: '유저를 탈퇴 처리 하시겠습니까?\n탈퇴 처리한 유저는 30일 이후 완전히 삭제됩니다.',
                    type: 'ok_cancel',
                    onConfirm: () => {
                      deleteUserMutation.mutate(_id);
                    },
                  });
                }}>
                탈퇴 처리
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: '역할',
      width: '140px',
      dataIndex: 'roles',
      align: 'center',
      render: (roles, record) => {
        const roleText = (roles || []).map((r: string) => {
          return ROLE_LABELS[r] || r;
        });
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              alignItems: 'center',
            }}>
            <div style={{ width: '110px' }}>{roleText.join(' / ')}</div>
            {(roles || []).some((role: Roles) => [Roles.TUTOR, Roles.SELLER].includes(role)) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                <Button
                  onClick={() => {
                    setSelectedTutorId(record._id);
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    파트너 프로필
                    {record.partnerProfile?.profileMainImageUrl && <ToolbarImageIcon height={18} />}
                  </div>
                </Button>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '활동분야',
      width: '120px',
      dataIndex: 'roles',
      align: 'center',
      render: (roles, record) => {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              alignItems: 'center',
            }}>
            <div style={{ whiteSpace: 'pre-wrap' }}>{`${
              (record.profile?.categorySubList || []).length > 0
                ? (record.profile?.categorySubList || []).join(',')
                : '-'
            }`}</div>
          </div>
        );
      },
    },
    {
      title: '고객센터',
      width: '120px',
      align: 'center',
      render: (record) => {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              alignItems: 'center',
            }}>
            <div>
              {record.profile?.mainCustomerService === 'chat' && (
                <div style={{ fontWeight: 'bold' }}>아이고고 앱 채팅</div>
              )}
              {((!record.profile?.mainCustomerService && record.profile?.customerServicePhone) ||
                record.profile?.mainCustomerService === 'phone') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Popover content={formatPhoneNumber(record.profile?.customerServicePhone) || '-'}>
                    <div
                      style={{
                        display: 'flex',
                        gap: '4px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        width: '90px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      <div>전화번호</div>
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          navigator.clipboard.writeText(record.profile?.customerServicePhone || '');
                          toast.success('전화번호가 클립보드에 복사되었습니다.');
                        }}
                        style={{ padding: '2px', minWidth: 'auto' }}
                      />
                    </div>
                  </Popover>
                </div>
              )}
              {(!record.profile?.mainCustomerService && record.profile?.customerServiceUrl) ||
                (record.profile?.mainCustomerService === 'url' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Popover content={record.profile?.customerServiceUrl || '-'}>
                      <div
                        style={{
                          display: 'flex',
                          gap: '4px',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          width: '90px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                        <div>URL</div>
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => {
                            navigator.clipboard.writeText(record.profile?.customerServiceUrl || '');
                            toast.success('URL이 클립보드에 복사되었습니다.');
                          }}
                          style={{ padding: '2px', minWidth: 'auto' }}
                        />
                      </div>
                    </Popover>
                  </div>
                ))}
            </div>
          </div>
        );
      },
    },
    {
      title: '이름',
      dataIndex: 'profile',
      align: 'center',
      width: '180px',
      render: (profile, record) => {
        return (
          <div>
            {record.roles?.includes(Roles.INSTRUCTOR) && (
              <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                <div>{record.partners?.map((partner) => partner.nickname).join(', ')}</div>
              </div>
            )}
            {profile?.nickname && <div>{profile?.nickname}</div>}
            <div>{profile?.name}</div>
          </div>
        );
      },
    },
    {
      title: '통계',
      dataIndex: 'statistics',
      align: 'center',
      width: '120px',
      render: (statistics: PartnerStatistics) => {
        const tooltipContent = (
          <div style={{ fontSize: '12px' }}>
            <div
              style={{
                fontWeight: 'bold',
                marginBottom: '8px',
                borderBottom: '1px solid #d9d9d9',
                paddingBottom: '4px',
              }}>
              총점: {statistics?.summary?.comprehensive_score || 0}점
            </div>
            <div>
              상품: {statistics?.product?.total_count || 0}개 (오픈: {statistics?.product?.open_count || 0}개)
            </div>
            <div>
              후기: {statistics?.review?.review_count || 0}개 (점수: {statistics?.review?.calculated_score || 0})
            </div>
            <div>
              채팅: {statistics?.chat?.total_messages || 0}개 (점수: {statistics?.chat?.calculated_score || 0})
            </div>
            <div>
              리포트: {statistics?.report?.total_reports || 0}개 (점수: {statistics?.report?.content_score || 0})
            </div>
          </div>
        );

        return (
          <Popover content={tooltipContent} title="상세 통계" placement="top">
            <div style={{ fontSize: '11px', lineHeight: '1.3', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px', fontWeight: 'bold' }}>
                <TrophyOutlined style={{ color: '#fa8c16', marginRight: '3px', fontSize: '12px' }} />
                <span style={{ color: '#fa8c16' }}>총점: {statistics?.summary?.comprehensive_score || 0}점</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                <ShoppingOutlined style={{ color: '#1890ff', marginRight: '3px', fontSize: '12px' }} />
                <span style={{ marginRight: '4px' }}>{statistics?.product?.total_count || 0}</span>
                <span style={{ color: '#52c41a', fontSize: '12px' }}>({statistics?.product?.open_count || 0})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                <StarOutlined style={{ color: '#faad14', marginRight: '3px', fontSize: '12px' }} />
                <span style={{ marginRight: '4px' }}>{statistics?.review?.review_count || 0}</span>
                <span style={{ color: '#722ed1', fontSize: '12px' }}>
                  ({statistics?.review?.calculated_score || 0})
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                <MessageOutlined style={{ color: '#13c2c2', marginRight: '3px', fontSize: '12px' }} />
                <span style={{ marginRight: '4px' }}>{statistics?.chat?.total_messages || 0}</span>
                <span style={{ color: '#722ed1', fontSize: '12px' }}>({statistics?.chat?.calculated_score || 0})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FileTextOutlined style={{ color: '#eb2f96', marginRight: '3px', fontSize: '12px' }} />
                <span style={{ marginRight: '4px' }}>{statistics?.report?.total_reports || 0}</span>
                <span style={{ color: '#722ed1', fontSize: '12px' }}>({statistics?.report?.content_score || 0})</span>
              </div>
            </div>
          </Popover>
        );
      },
    },
    {
      title: '우수파트너',
      dataIndex: 'excellentPartner',
      align: 'center',
      width: '150px',
      render: (excellentPartner: ExcellentPartner | undefined, record) => {
        return (
          <div style={{ fontSize: '11px', lineHeight: 1.4 }}>
            <Switch
              size="small"
              checked={excellentPartner?.selected === true}
              loading={excellentPartnerMutation.isPending && excellentPartnerMutation.variables?.userId === record._id}
              onChange={(checked) => {
                excellentPartnerMutation.mutate({ userId: record._id, selected: checked });
              }}
            />
            <div style={{ marginTop: 4, fontWeight: 'bold' }}>
              {excellentPartner?.score ?? 0}점
              {excellentPartner?.source === 'manual' ? ' · 수동' : excellentPartner?.selected ? ' · 자동' : ''}
            </div>
            <Popover content={<ExcellentPartnerCriteriaTable excellentPartner={excellentPartner} />}>
              <div style={{ color: '#722ed1', cursor: 'pointer' }}>기준 보기</div>
            </Popover>
          </div>
        );
      },
    },
    {
      title: '주소',
      dataIndex: 'profile',
      width: '120px',
      align: 'center',
      render: (profile) => {
        return (
          <Popover
            content={profile?.address ? `${profile.address?.address} ${profile.address?.address2}` : '주소 없음'}>
            <div>{getAddressText({ availableArea: { specificAddress: profile?.address } })}</div>
          </Popover>
        );
      },
    },
    {
      title: '회원가입 구분',
      dataIndex: 'services',
      width: '100px',
      render: (services, record) => {
        const loginInfo: { names: string[]; emails: string[] } = { names: [], emails: [] };
        if (services?.kakao) {
          loginInfo.names.push('카카오');
          loginInfo.emails.push(services.kakao.email || '');
        }

        if (services?.naver) {
          loginInfo.names.push('네이버');
          loginInfo.emails.push(services.naver.email || '');
        }

        if (services?.apple) {
          loginInfo.names.push('애플');
          loginInfo.emails.push(services.apple.email || '');
        }

        if (loginInfo.names.length === 0 && record.emails) {
          loginInfo.names.push('이메일');
          loginInfo.emails.push(record.emails?.[0]?.address || '');
        }
        return (
          <div>
            <div>{loginInfo.names.join(', ')}</div>
            <Popover content={loginInfo.emails.join('\n')}>
              <div
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-all',
                  width: '90px',
                }}>
                {loginInfo.emails.join('\n')}
              </div>
            </Popover>
          </div>
        );
      },
    },
    {
      title: '전화번호',
      dataIndex: 'profile',
      align: 'center',
      width: '136px',
      render: (profile) => {
        if (!profile?.phone) {
          return '-';
        }
        return (
          <Button
            onClick={() => {
              navigator.clipboard.writeText(profile.phone);
              toast.info('전화번호를 클립보드에 복사했습니다.', {
                position: 'top-center',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                pauseOnFocusLoss: false,
                draggable: true,
                progress: undefined,
                theme: 'light',
              });
            }}>
            {profile.phone}
          </Button>
        );
      },
    },
    {
      title: '라운지 글쓰기',
      width: '120px',
      align: 'center',
      render: (record) => {
        let policyText = '작성 기간 제한';
        if (record.profile?.contentWrite?.deniedReason !== 'period-policy') {
          policyText = record.profile?.contentWrite?.deniedReason || '-';
        }

        return (
          <div>
            <div
              className="membership-badge"
              style={{
                background: record.profile?.contentWrite?.denied ? '#FF4D4F' : '#52C41A',
                color: 'white',
                border: 'none',
                padding: '2px 8px',
                borderRadius: '4px',
                display: 'inline-block',
              }}>
              {record.profile?.contentWrite?.denied ? '제한' : '허용'}
            </div>
            {record.profile?.contentWrite?.denied && <div>{policyText}</div>}
            {record.profile?.contentWrite?.denied && (
              <div>{moment(record.profile?.contentWrite?.deniedUntil).format('YYYY.MM.DD HH:mm')}에 해제</div>
            )}
          </div>
        );
      },
    },
    {
      title: '가입',
      width: '120px',
      align: 'center',
      render: (record) => {
        return (
          <div>
            <div>{moment(record.createdAt).format('YYYY.MM.DD')}</div>
            {record.deletedAt && <div style={{ color: 'red' }}>{moment(record.deletedAt).format('YYYY.MM.DD')}</div>}
          </div>
        );
      },
    },
    {
      title: '메모',
      dataIndex: 'deletedAt',
      render: (memo) => {
        if (!memo) {
          return <div style={{ width: '28px' }}>-</div>;
        }
        return memo;
      },
    },
  ];

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <UserEditModal
        open={!!editUserId}
        userId={editUserId}
        needRefetch={() => {
          userSearchQuery.refetch();
        }}
        onClose={() => {
          setEditUserId(undefined);
        }}></UserEditModal>
      <UserMembershipEditModal
        open={!!editMembershipUserId}
        userId={editMembershipUserId}
        needRefetch={() => {
          userSearchQuery.refetch();
        }}
        onClose={() => {
          setEditMembershipUserId(undefined);
        }}></UserMembershipEditModal>
      <UserCreateModal
        open={showUserCreate}
        onClose={() => {
          setShowUserCreate(false);
        }}
        needRefetch={() => {
          userSearchQuery.refetch();
        }}></UserCreateModal>
      <UserPointEditModal
        open={!!pointEditUser}
        user={pointEditUser}
        onClose={() => {
          setPointEditUser(undefined);
        }}
        needRefetch={() => {
          userSearchQuery.refetch();
        }}></UserPointEditModal>
      <Modal
        open={showWarningProductsModal}
        onCancel={() => setShowWarningProductsModal(false)}
        title="존재하지 않는 파트너의 판매중 상품"
        footer={<Button onClick={() => setShowWarningProductsModal(false)}>닫기</Button>}
        width={800}>
        <div>
          {partnersCountsQuery.data?.data?.warningProducts &&
          partnersCountsQuery.data.data.warningProducts.length > 0 ? (
            <div>
              <p style={{ marginBottom: '16px', color: '#666' }}>
                총 {partnersCountsQuery.data.data.warningProducts.length}개의 상품이 존재하지 않는 파트너에 의해
                판매되고 있습니다.
              </p>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {partnersCountsQuery.data.data.warningProducts.map((product: ProductModel) => (
                  <div
                    key={product._id}
                    style={{
                      padding: '12px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      backgroundColor: '#fafafa',
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '8px',
                      }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                          파트너: {product.sellerName || '알 수 없음'}
                        </div>
                        <div style={{ color: '#666', fontSize: '14px' }}>상품명: {product.title}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => {
                            navigator.clipboard.writeText(product._id);
                            toast.dark('상품 ID가 복사되었습니다.', {
                              position: 'top-center',
                              autoClose: 2000,
                            });
                          }}>
                          ID 복사
                        </Button>
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => {
                            navigator.clipboard.writeText(product.sellerId);
                            toast.dark('파트너 ID가 복사되었습니다.', {
                              position: 'top-center',
                              autoClose: 2000,
                            });
                          }}>
                          파트너 ID 복사
                        </Button>
                      </div>
                    </div>
                    <div style={{ fontSize: '12px', color: '#999' }}>상품 ID: {product._id}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>경고 상품이 없습니다.</div>
          )}
        </div>
      </Modal>
      <div className="page-header">
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div>파트너 관리</div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge
              count={`우수파트너: ${partnersCountsQuery.data?.data?.excellentPartnerCount || 0}${
                partnersCountsQuery.data?.data?.excellentPartnerEvaluatedAt
                  ? ` · ${dayjs(partnersCountsQuery.data.data.excellentPartnerEvaluatedAt).format('MM-DD HH:mm')}`
                  : ''
              }`}
              style={{
                backgroundColor: '#722ed1',
                color: 'white',
                fontSize: '12px',
                borderRadius: '12px',
                border: 'none',
                fontWeight: 'bold',
              }}
            />
            <Badge
              count={`전체: ${partnersCountsQuery.data?.data?.totalPartners || 0}`}
              style={{
                backgroundColor: '#424242',
                color: 'white',
                fontSize: '12px',
                borderRadius: '12px',
                border: 'none',
                fontWeight: 'bold',
              }}
            />
            <Badge
              count={`상품 판매중: ${partnersCountsQuery.data?.data?.partnersWithOpenProducts || 0}(커머스: ${
                partnersCountsQuery.data?.data?.partnersWithOpenCommerceProducts || 0
              }, 클래스: ${partnersCountsQuery.data?.data?.partnersWithOpenClassProducts || 0})`}
              style={{
                backgroundColor: '#52c41a',
                color: 'white',
                fontSize: '12px',
                borderRadius: '12px',
                border: 'none',
                fontWeight: 'bold',
              }}
            />
            <Badge
              count={`상품 판매안함: ${partnersCountsQuery.data?.data?.partnersWithProductsButNotOpen || 0}(커머스: ${
                partnersCountsQuery.data?.data?.partnersWithCommerceProductsButNotOpen || 0
              }, 클래스: ${partnersCountsQuery.data?.data?.partnersWithClassProductsButNotOpen || 0}, 탈퇴: ${
                partnersCountsQuery.data?.data?.notSellingSellerIdsMissingFromUsers || 0
              })`}
              style={{
                backgroundColor: '#fa8c16',
                color: 'white',
                fontSize: '12px',
                borderRadius: '12px',
                border: 'none',
                fontWeight: 'bold',
              }}
            />
            <Badge
              count={`상품 없음: ${partnersCountsQuery.data?.data?.partnersWithoutProducts || 0}(커머스: ${
                partnersCountsQuery.data?.data?.commercePartnersWithoutProducts || 0
              }, 클래스: ${partnersCountsQuery.data?.data?.classPartnersWithoutProducts || 0})`}
              style={{
                backgroundColor: '#242424',
                color: 'white',
                fontSize: '12px',
                borderRadius: '12px',
                border: 'none',
                fontWeight: 'bold',
              }}
            />
            {partnersCountsQuery.data?.data?.warningProducts &&
              partnersCountsQuery.data.data.warningProducts.length > 0 && (
                <div onClick={() => setShowWarningProductsModal(true)} style={{ cursor: 'pointer' }}>
                  <Badge
                    count={`존재하지 않는 파트너 상품 경고: ${partnersCountsQuery.data.data.warningProducts.length}개`}
                    style={{
                      backgroundColor: '#f5222d',
                      color: 'white',
                      fontSize: '12px',
                      borderRadius: '12px',
                      border: 'none',
                      fontWeight: 'bold',
                    }}
                  />
                </div>
              )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            type="primary"
            loading={runSelectionMutation.isPending || selectionRunning}
            onClick={() => {
              Modal.confirm({
                title: '우수파트너 선정을 실행할까요?',
                content: '전체 파트너의 점수와 자동 선정 결과가 다시 계산됩니다. 수동으로 고정한 선정은 유지됩니다.',
                okText: '실행',
                cancelText: '취소',
                onOk: () => runSelectionMutation.mutateAsync(),
              });
            }}>
            우수파트너 선정 실행
          </Button>
          <div style={{ marginLeft: '10px' }}>
            <DatePicker
              onChange={onChangeStartDate}
              value={dayjs(utcTimeToLocalISOString(store.searchQuery.startDate as any))}
            />
          </div>
          <div style={{ marginLeft: '8px' }}>
            <DatePicker
              onChange={onChangeEndDate}
              value={dayjs(utcTimeToLocalISOString(store.searchQuery.endDate as any))}
            />
          </div>
        </div>
      </div>
      <Modal
        open={!!selectedTutorId}
        onCancel={() => {
          setSelectedTutorId(undefined);
        }}
        footer={
          <Button
            onClick={() => {
              setSelectedTutorId(undefined);
            }}>
            닫기
          </Button>
        }>
        <div>
          <OverlayScrollbarsComponent
            defer
            options={{ scrollbars: { autoHide: 'scroll' } }}
            style={{
              backgroundColor: '#ffffff',
              marginTop: 0,
              position: 'relative',
              height: 'calc(100dvh - 240px)',
              overflow: 'auto',
            }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', height: '44px', gap: '8px', alignItems: 'center', fontWeight: 'bold' }}>
                <div>{`${userInfo?.profile?.name}${
                  userInfo?.profile?.nickname ? `(${userInfo?.profile?.nickname})` : ''
                }`}</div>
                <div>
                  {userInfo?.profile?.contract?.licensePhoto ? (
                    <PhotoView src={cdnImageUrl(userInfo?.profile?.contract?.licensePhoto as string)}>
                      <Button>신분증</Button>
                    </PhotoView>
                  ) : (
                    <div>신분증 없음</div>
                  )}
                </div>
                <div>
                  {userInfo?.profile?.contract?.bankAccountPhoto ? (
                    <PhotoView src={cdnImageUrl(userInfo?.profile?.contract?.bankAccountPhoto as string)}>
                      <Button>통장</Button>
                    </PhotoView>
                  ) : (
                    <div>통장 없음</div>
                  )}
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <img
                  style={{
                    width: '88px',
                    height: '88px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    position: 'absolute',
                    left: '20px',
                    bottom: '-20px',
                  }}
                  src={userInfo?.profile?.imageUrl ? cdnImageUrl(userInfo?.profile.imageUrl) : TutorDefaultAvatar}
                  alt=""
                />
                <img
                  src={
                    userInfo?.partnerProfile?.profileMainImageUrl
                      ? cdnImageUrl(userInfo?.partnerProfile?.profileMainImageUrl)
                      : headerImageRef.current
                  }
                  style={{ objectFit: 'cover', width: '100%' }}
                  alt="partner-profile-header"
                />
              </div>
              <div style={{ marginTop: '44px', padding: '0 20px' }}>
                <div
                  style={{
                    overflow: 'hidden',
                    color: 'var(--Neutral-100, #242424)',
                    textOverflow: 'ellipsis',
                    fontFamily: 'Pretendard',
                    fontSize: '28px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                  }}>
                  {userInfo?.profile?.name}입니다.
                </div>
                {userInfo?.partnerProfile?.intro && (
                  <div style={{ marginTop: '8px' }}>
                    <div
                      style={{
                        color: 'var(--Neutral-80, #66686B)',
                        fontFamily: 'Pretendard',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        whiteSpace: 'pre-wrap',
                      }}>
                      <LinesEllipsis
                        text={userInfo?.partnerProfile?.intro}
                        maxLine={expandGreeting ? 100 : 3}
                        onReflow={(value) => {
                          setIsGreetingClamped(value.clamped || expandGreeting);
                        }}
                        trimRight></LinesEllipsis>
                    </div>
                    {isGreetingClamped && (
                      <button
                        className="link-button"
                        style={{
                          color: 'var(--Blue-60, #1890FF)',
                          fontFamily: 'Pretendard',
                          fontSize: '14px',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          textDecorationLine: 'underline',
                          textDecorationStyle: 'solid',
                          textDecorationSkipInk: 'none',
                          textDecorationThickness: 'auto',
                          textUnderlineOffset: 'auto',
                          textUnderlinePosition: 'from-font',
                          marginTop: '8px',
                        }}
                        onClick={() => {
                          setExpandGreeting((value) => !value);
                        }}>
                        {expandGreeting ? <div>접기</div> : <div>더보기</div>}
                      </button>
                    )}
                  </div>
                )}
              </div>
              {userInfo?.partnerProfile?.mainActivityZone && (
                <>
                  <Divider style={{ margin: '20px 0' }} />
                  <div
                    style={{
                      color: 'var(--Neutral-100, #242424)',
                      fontFamily: 'Pretendard',
                      fontSize: '20px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      padding: '0 20px',
                    }}>
                    <div>주요 활동 지역</div>
                  </div>
                  <div
                    style={{
                      overflow: 'hidden',
                      color: 'var(--Neutral-80, #66686B)',
                      textOverflow: 'ellipsis',
                      fontFamily: 'Pretendard',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      marginTop: '8px',
                      padding: '0 20px',
                    }}>
                    {userInfo?.partnerProfile?.mainActivityZone
                      .map((item) => {
                        return `${item.city} ${item.district}`;
                      })
                      .join(', ')}
                  </div>
                </>
              )}

              <Divider style={{ margin: '20px 0' }} />
              <button
                className="link-button"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: 'var(--Neutral-100, #242424)',
                  fontFamily: 'Pretendard',
                  fontSize: '20px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  padding: '0 20px',
                }}
                onClick={() => {
                  setShowExperiences((value) => !value);
                }}>
                활동 내역
                {showExperiences ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </button>
              {showExperiences && (
                <div style={{ padding: '20px' }}>
                  <div className={'partner-academy-content'} style={{ paddingBottom: '18px', paddingRight: '20px' }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        marginBottom: '13px',
                        alignItems: 'center',
                      }}>
                      <div
                        style={{
                          overflow: 'hidden',
                          color: 'var(--Neutral-100, #242424)',
                          textOverflow: 'ellipsis',
                          fontFamily: 'Pretendard',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 700,
                        }}>
                        학력
                      </div>
                    </div>
                    {partnerAcamemics.map((item, index) => {
                      return (
                        <div key={`academic-${index}`}>
                          <div
                            style={{
                              flexDirection: 'row',
                              marginBottom: '10px',
                              alignItems: 'center',
                            }}>
                            <div
                              style={{
                                flex: 1,
                                paddingLeft: '10px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginLeft: 0,
                              }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}>
                                <div
                                  style={{
                                    backgroundColor: '#dadcdf',
                                    width: '6px',
                                    height: '6px',
                                    borderStyle: 'solid',
                                    borderRadius: '6px',
                                    marginRight: '13px',
                                    marginLeft: 0,
                                    marginTop: 7,
                                  }}
                                />
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                  }}>
                                  {`${item.school} ${item.graduation} (${item.degree ? degreeValue(item.degree) : ''})`}
                                  {item.approvalState === 'confirmApproval' && <QualifiedIcon />}
                                  <Select
                                    style={{ width: '120px' }}
                                    value={item.approvalState}
                                    options={[
                                      { value: ProfileApprovalState.WAIT, label: '승인 대기' },
                                      { value: ProfileApprovalState.CONFIRM, label: '승인' },
                                      { value: ProfileApprovalState.NOTHING, label: '첨부파일 없음' },
                                      { value: ProfileApprovalState.DENIED, label: '반려' },
                                    ]}
                                    onChange={(value) => {
                                      item.approvalState = value;
                                      if (selectedTutorId) {
                                        const newValues = [...partnerAcamemics];
                                        newValues[index] = { ...item, approvalState: value };

                                        partnerProfileMutation.mutate({
                                          userId: selectedTutorId,
                                          body: { academics: newValues },
                                        });
                                      }
                                    }}
                                  />
                                </div>
                                {item.imageUrl && (
                                  <button
                                    className="link-button"
                                    onClick={() => {
                                      window.open(cdnImageUrl(item.imageUrl), '_blank');
                                    }}>
                                    <AttachmentIcon />
                                  </button>
                                )}
                              </div>
                              {item.year && item.graduation && item.degree && (
                                <div
                                  style={{
                                    fontSize: '12px',
                                    color: '#9b9da0',
                                    marginLeft: '20px',
                                    marginTop: '4px',
                                  }}>
                                  {item.year} - {item.graduation}{' '}
                                  {item.degree ? '(' + degreeValue(item.degree) + ' 과정)' : ''}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {partnerAcamemics.length === 0 && (
                      <div
                        style={{
                          color: '#9b9da0',
                          fontSize: '15px',
                          lineHeight: '22px',
                          marginBottom: '10px',
                        }}>
                        파트너님께서 학력 등록을 준비하고 계세요.
                      </div>
                    )}
                  </div>

                  <div className={'partner-career-content'} style={{ paddingBottom: '18px', paddingRight: '20px' }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        marginBottom: '13px',
                        alignItems: 'center',
                      }}>
                      <div
                        style={{
                          overflow: 'hidden',
                          color: 'var(--Neutral-100, #242424)',
                          textOverflow: 'ellipsis',
                          fontFamily: 'Pretendard',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 700,
                        }}>
                        경력
                      </div>
                    </div>
                    {partnerCareers.map((item, index) => {
                      return (
                        (item.approvalState === 'confirmApproval' ||
                          item.approvalState === 'beforeApproval' ||
                          item.approvalState === 'waitApproval') && (
                          <div key={`career-${index}`}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                marginBottom: '10px',
                                alignItems: 'center',
                              }}>
                              <div style={{ flex: 1, paddingLeft: '10px', justifyContent: 'center' }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                  }}>
                                  <div
                                    style={{
                                      backgroundColor: '#dadcdf',
                                      width: '6px',
                                      height: '6px',
                                      borderStyle: 'solid',
                                      borderRadius: '6px',
                                      marginRight: '13px',
                                      marginLeft: 0,
                                      marginTop: '8px',
                                    }}
                                  />
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                      alignItems: 'center',
                                      flexWrap: 'wrap',
                                    }}>
                                    {(item.career || '').split(' ').map((item, idx) => {
                                      return (
                                        <div
                                          key={`company-item-${idx}`}
                                          style={{
                                            fontSize: '15px',
                                            lineHeight: '22px',
                                            marginRight: '6px',
                                            color: '#424242',
                                          }}>
                                          {item}
                                        </div>
                                      );
                                    })}
                                    {item.approvalState === 'confirmApproval' && <QualifiedIcon />}
                                    <Select
                                      style={{ width: '120px' }}
                                      value={item.approvalState}
                                      options={[
                                        { value: ProfileApprovalState.WAIT, label: '승인 대기' },
                                        { value: ProfileApprovalState.CONFIRM, label: '승인' },
                                        { value: ProfileApprovalState.NOTHING, label: '첨부파일 없음' },
                                        { value: ProfileApprovalState.DENIED, label: '반려' },
                                      ]}
                                      onChange={(value) => {
                                        item.approvalState = value;

                                        if (selectedTutorId) {
                                          const newValues = [...partnerCareers];
                                          newValues[index] = { ...item, approvalState: value };

                                          partnerProfileMutation.mutate({
                                            userId: selectedTutorId,
                                            body: { careers: newValues },
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                  {item.imageUrl && (
                                    <button
                                      className="link-button"
                                      onClick={() => {
                                        window.open(cdnImageUrl(item.imageUrl), '_blank');
                                      }}>
                                      <AttachmentIcon />
                                    </button>
                                  )}
                                </div>
                                {(item.startYearMonth || item.period) && (
                                  <div
                                    style={{
                                      fontSize: '12px',
                                      color: '#9b9da0',
                                      marginLeft: '20px',
                                      marginTop: '4px',
                                    }}>
                                    {item.startYearMonth
                                      ? `${item.startYearMonth.replace('-', '.')} - ${item.endYearMonth?.replace(
                                          '-',
                                          '.',
                                        )}`
                                      : item.period}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      );
                    })}
                    {partnerCareers.length === 0 && (
                      <div
                        style={{
                          color: '#9b9da0',
                          fontSize: '15px',
                          lineHeight: '22px',
                          marginBottom: '10px',
                        }}>
                        파트너님께서 경력 등록을 준비하고 계세요.
                      </div>
                    )}
                  </div>

                  <div
                    className={'partner-certificate-content'}
                    style={{ paddingBottom: '18px', paddingRight: '20px' }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginBottom: '13px',
                      }}>
                      <div
                        style={{
                          overflow: 'hidden',
                          color: 'var(--Neutral-100, #242424)',
                          textOverflow: 'ellipsis',
                          fontFamily: 'Pretendard',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 700,
                        }}>
                        자격증
                      </div>
                    </div>
                    {partnerCertificates.map((item, index) => {
                      return (
                        (item.approvalState === 'confirmApproval' ||
                          item.approvalState === 'beforeApproval' ||
                          item.approvalState === 'waitApproval') && (
                          <div key={`certificate-${index}`}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                marginBottom: '10px',
                                alignItems: 'center',
                              }}>
                              <div style={{ flex: 1, paddingLeft: '10px', justifyContent: 'center' }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                  }}>
                                  <div
                                    style={{
                                      backgroundColor: '#dadcdf',
                                      width: '6px',
                                      height: '6px',
                                      borderStyle: 'solid',
                                      borderRadius: '6px',
                                      marginRight: '13px',
                                      marginLeft: 0,
                                      marginTop: '8px',
                                    }}
                                  />
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                      alignItems: 'center',
                                      flexWrap: 'wrap',
                                    }}>
                                    {(item.name || '').split(' ').map((item, idx) => {
                                      return (
                                        <div
                                          key={`name-item-${idx}`}
                                          style={{
                                            fontSize: '15px',
                                            color: '#424242',
                                            marginRight: '6px',
                                            lineHeight: '22px',
                                          }}>
                                          {item}
                                        </div>
                                      );
                                    })}
                                    {item.approvalState === 'confirmApproval' && <QualifiedIcon />}
                                    <Select
                                      style={{ width: '120px' }}
                                      value={item.approvalState}
                                      options={[
                                        { value: ProfileApprovalState.WAIT, label: '승인 대기' },
                                        { value: ProfileApprovalState.CONFIRM, label: '승인' },
                                        { value: ProfileApprovalState.NOTHING, label: '첨부파일 없음' },
                                        { value: ProfileApprovalState.DENIED, label: '반려' },
                                      ]}
                                      onChange={(value) => {
                                        item.approvalState = value;

                                        if (selectedTutorId) {
                                          const newValues = [...partnerCertificates];
                                          newValues[index] = { ...item, approvalState: value };

                                          partnerProfileMutation.mutate({
                                            userId: selectedTutorId,
                                            body: { certificates: newValues },
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                  {item.imageUrl && (
                                    <button
                                      className="link-button"
                                      onClick={() => {
                                        window.open(cdnImageUrl(item.imageUrl), '_blank');
                                      }}>
                                      <AttachmentIcon />
                                    </button>
                                  )}
                                </div>
                                {item.issuer && (
                                  <div
                                    style={{
                                      fontSize: '12px',
                                      color: '#9b9da0',
                                      marginLeft: '20px',
                                      marginTop: '4px',
                                    }}>
                                    {item.issuer} -{' '}
                                    {item.issuanceYearMonth ? `${item.issuanceYearMonth.replace('-', '.')}` : ''}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      );
                    })}
                    {partnerCertificates.length === 0 && (
                      <div
                        style={{
                          color: '#9b9da0',
                          fontSize: '15px',
                          lineHeight: '22px',
                          marginBottom: '10px',
                        }}>
                        파트너님께서 자격증 등록을 준비하고 계세요.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </OverlayScrollbarsComponent>
        </div>
      </Modal>
      <div style={{ display: 'flex' }}>
        <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <div style={{ display: 'flex', gap: '12px', width: '100%', height: '52px', alignItems: 'center' }}>
            <FilterSelect
              inactiveValue="all"
              value={store.searchQuery.type || 'all'}
              style={{ width: 120 }}
              onChange={(value) => {
                store.updateSearchQuery({ type: value });
              }}
              options={[
                { value: 'all', label: '모든 역할' },
                ...Object.keys(PARTNER_ROLE_LABELS).map((key) => {
                  return { value: key, label: PARTNER_ROLE_LABELS[key] };
                }),
              ]}
            />
            <FilterSelect
              inactiveValue="all"
              value={store.searchQuery.profileStatus || 'all'}
              style={{ width: 180 }}
              onChange={(value) => {
                store.updateSearchQuery({ profileStatus: value });
              }}
              options={[
                { value: 'all', label: '프로필 상태 전체' },
                { value: ProfileApprovalState.WAIT, label: '승인 대기' },
                { value: ProfileApprovalState.CONFIRM, label: '승인' },
                { value: ProfileApprovalState.NOTHING, label: '첨부파일 없음' },
              ]}
            />
            <FilterSelect
              inactiveValue="all"
              value={store.searchQuery.partnerFilter || 'all'}
              style={{ width: 180 }}
              onChange={(value) => {
                store.updateSearchQuery({ partnerFilter: value });
              }}
              options={[
                { value: 'all', label: '파트너 필터 전체' },
                { value: 'selling', label: '상품 판매중' },
                { value: 'not-selling', label: '상품 판매하지 않음' },
                { value: 'no-products', label: '등록한 상품 없음' },
              ]}
            />
            <FilterSelect
              inactiveValue="all"
              value={store.searchQuery.excellentPartner || 'all'}
              style={{ width: 180 }}
              onChange={(value) => {
                store.updateSearchQuery({ excellentPartner: value, page: 0 });
              }}
              options={[
                { value: 'all', label: '우수파트너 전체' },
                { value: 'yes', label: '우수파트너만' },
                { value: 'no', label: '우수파트너 아님' },
              ]}
            />
            <FilterSelect
              inactiveValue="createdAt"
              value={store.searchQuery.sort || 'createdAt'}
              style={{ width: 180 }}
              onChange={(value) => {
                store.updateSearchQuery({ sort: value });
              }}
              options={[
                { value: 'createdAt', label: '최근 가입일 순' },
                { value: 'updatedAt', label: '최근 업데이트 순' },
                { value: 'score_desc', label: '총점 내림차순' },
                { value: 'excellentScore_desc', label: '우수파트너 점수 내림차순' },
                { value: 'reviewCount_desc', label: '후기 수 내림차순' },
                { value: 'chatCount_desc', label: '채팅 수 내림차순' },
                { value: 'reportCount_desc', label: '리포트 수 내림차순' },
                { value: 'productCount_desc', label: '상품 수 내림차순' },
                { value: 'productOpenCount_desc', label: '판매중 상품 수 내림차순' },
                { value: 'reviewCount_asc', label: '후기 수 오름차순' },
                { value: 'chatCount_asc', label: '채팅 수 오름차순' },
                { value: 'reportCount_asc', label: '리포트 수 오름차순' },
                { value: 'productCount_asc', label: '상품 수 오름차순' },
                { value: 'productOpenCount_asc', label: '판매중 상품 수 오름차순' },
              ]}
            />
            <Input
              className={filterActiveClassName(!!store.searchQuery.keyword)}
              style={{ width: '240px' }}
              value={searchKeyword}
              placeholder={'검색어 입력 후 엔터'}
              onChange={(e) => {
                setSearchKeyword(e.target.value);
              }}
              onKeyUp={(e) => {
                if (e.code === 'Enter' && searchKeyword) {
                  store.updateSearchQuery({ keyword: searchKeyword });
                }
              }}></Input>
            <Button
              onClick={() => {
                setSearchKeyword(undefined);
                store.resetSearchQuery();
              }}>
              검색 초기화
            </Button>
            <div style={{ fontWeight: 'bold' }}>총 검색결과: {store.searchResultContainer.total}</div>
            <div style={{ display: 'flex', marginLeft: 'auto', gap: '12px', alignItems: 'center' }}>
              <Button
                onClick={() => {
                  setShowUserCreate(true);
                }}>
                신규 유저 생성
              </Button>
              <Button
                style={{ marginLeft: '10px' }}
                onClick={() => {
                  getExcelFile();
                }}>
                검색결과 엑셀 파일 다운로드
              </Button>
            </div>
          </div>
          <Table
            columns={TABLE_COLUMNS}
            scroll={{ x: 'max-content', y: 'calc(100dvh - 280px)' }}
            style={{ whiteSpace: 'pre-wrap' }}
            rowKey={(record) => record?._id}
            dataSource={store.searchResultContainer.contents.filter((user) => user?._id)}
            pagination={{
              current: (store.searchResultContainer.page || 0) + 1,
              pageSize: store.searchResultContainer.size || 20,
              total: store.searchResultContainer.total,
            }}
            loading={userSearchQuery.isLoading}
            onChange={(e) => {
              store.updateSearchQuery({ page: (e.current || 2) - 1, size: e.pageSize || 20 });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PartnerUsersPage;
